import { IReactionDisposer, reaction, observable } from 'mobx';
import { isSpecialActivity } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/anim-utils';
import { IMediaManager } from '@src/slide/application/service/media-manager/media-manager.interface';
/**
 * 运营活动音频播放规则:
 *
 * 文档有音频，就播放，具体播放哪个要看是否有配置
 * 文档没音频，就不播放，不管是否有配置，都不播放
 *
 * 1、文档有音频，七彩石有配置音频，则播放七彩石配置的音频。
 *    若七彩石配置拉取失败，则播放文档音频。
 * 2、文档有音频，七彩石没有配置音频，则播放文档音频。
 * 3、文档没有音频，七彩石有配置音频，则不播放音频。
 * 4、文档没有音频，七彩石没有配置音频，则不播放音频。
 */
interface IAudioPlayData {
    mediaManager: IMediaManager|undefined;
    isLoop: boolean;
}
interface IAudioControl {
    initData(params: IAudioPlayData): void | undefined;
    playAudio(): void | undefined;
    setAudioUrl(url: string): void;
    disposeAudio(): void;
}

enum AudioReadyState {
    HAVE_NOTHING, // 没有关于音频文件的信息可用。
    HAVE_METADATA, // 音频文件的元数据已加载。
    HAVE_CURRENT_DATA, // 已加载足够的音频文件以开始播放当前帧。
    HAVE_FUTURE_DATA, // 已加载足够的音频文件以播放当前和下一帧。
    HAVE_ENOUGH_DATA, // 整个音频文件已加载，可以无间断地播放。
}

const AUDIO_PAUSE_STATE = 'pause';
const AUDIO_LOADED = 'loadeddata';
const PAGE_VISIBLE = 'visible';
const PAGE_VISIBLE_CHANGE = 'visibilitychange';
const PAGE_FOCUS = 'focus';
const PAGE_BLUR = 'blur';
export default class AudioControlManager implements IAudioControl {
    public static instance: AudioControlManager;
    public static getInstance(): AudioControlManager {
        if (!this.instance) {
            this.instance = new AudioControlManager();
        }
        return this.instance;
    }

    // audio dom元素
    public audio: HTMLAudioElement | null;

    private mediaManager: IMediaManager;

    // 是否总是播放第一个音频
    private isAlwaysPlayFirstAudio = false;

    // 销毁函数
    private disposeAudioReaction: IReactionDisposer;

    // 是否已经初始化
    private isInit = false;

    // 是否禁止自动播放（非此次运营活动禁止自动播放）
    private isForbidAutoPlay = !isSpecialActivity;

    // 销毁函数
    private disposeConfigReaction: IReactionDisposer;

    // 从配置中心拉取的audio url
    @observable private audioUrlFromConfig = '';

    // 音频是否已经播放
    private audioHasPlayed = false;

    // null: 配置拉取中； true: 已拉取配置且拉取成功，false: 已拉取配置，但拉取失败
    private configFetched: null | boolean = null;


    // 活动页面是否active
    private isPageFocus = true;

    /**
     * 初始化音频数据
     * @param param0 IAudioPlayData
     * isLoop是否循环播放
     */
    public initData({ mediaManager, isLoop }: IAudioPlayData): void | undefined {
        if (!mediaManager || this.isForbidAutoPlay || this.isInit) return;
        this.mediaManager = mediaManager;
        if (!this.disposeAudioReaction) {
            this.disposeAudioReaction = this.createAudioReaction(isLoop);
        }
        this.isInit = true;
        this.addPageChangeListener();
    }

    /**
     * 播放音频
     * 1、chrome移动端模拟器 播放音频有坑，一定要在真机上验证
     * 坑1：模拟器中只要同一音频被播放过一次，下次播放就不会被阻止，无论这两次播放是否来自交互。
     * 但是ios真机中，音频被播放过一次后，如果希望第二次播放不被阻止，那么这两次播放中必须有一次来自于交互，否则播不了。
     * 坑2：元素卸载后，模拟器上有些事件会继续响应，但真机上其实并不会响应
     *
     * 2、由于安卓和ios对音频的自动播放限制策略不同，因此ios和安卓真机上都要验证
     *
     * 3、微信内的播放限制策略和手机自带浏览器播放策略不同，需要在真机上验证
     * ios微信内可以自动播放
     * ios safari 点击一次可播放
     * 安卓微信内点击一次可播放
     * 安卓自带浏览器点击一次可播放
     */
    public playAudio(): void | undefined {
        if (!this.audio) return;
        this.playWhenSlideAudioCreated();
    }

    /**
     * 设置从配置平台拉取到的音频地址
     * @param url 音频链接 string | null
     */
    public setAudioUrl(url: string): void {
        this.audioUrlFromConfig = url;
    }

    /**
     * 设置七彩石配置获取状态
     * @param value true 配置拉取成功，false 配置拉取失败
     */
    public setConfigStatus(value: boolean) {
        this.configFetched = value;
    }

    /**
     * 销毁audio相关的监听及数据
     */
    public disposeAudio(): void {
        if (!this.isInit) return;
        this.removePageChangeListener();
        this.disposeAudioReaction?.();
        this.disposeConfigReaction?.();
        this.audio?.removeEventListener(AUDIO_LOADED, this.onAudioLoaded);
        this.audio?.removeEventListener(AUDIO_PAUSE_STATE, this.onAudioPause);
        this.audio?.pause();
        this.audio = null;
    }

    /**
     * 文档自身有audio，且已创建，处理音频播放
     * 此时需要考虑七彩石的配置是否拉取成功, 以及音频是否加载成功
     */
    private playWhenSlideAudioCreated() {
        if (!this.audio) return;
        // 配置在拉取中，但没有返回结果, 此时需要对拉取的配置进行监听
        if (this.configFetched === null) {
            this.disposeConfigReaction = reaction(
                () => this.audioUrlFromConfig,
                (url) => {
                    this.playWhenFetchSuccess(url);
                },
            );
            return;
        }

        // 七彩石已拉取且拉取成功
        if (this.configFetched) {
            this.playWhenFetchSuccess(this.audioUrlFromConfig);
            return;
        }
        // 七彩石已拉取，但拉取失败，此时configFetched为false
        // 若文档音频已经加载成功可以播放，则播放文档音频
        // 若文档音频尚未加载成功，则不做处理，因为已经在init时监听了音频加载完成
        if (this.audio.readyState < AudioReadyState.HAVE_CURRENT_DATA) return;
        this.playAudioActually();
    }


    /**
     * 七彩石配置拉取成功后，处理音频播放
     * @param url string | null
     */
    private playWhenFetchSuccess(url) {
        if (!this.audio) return;
        // 若配置了audio，则播放配置audio，否则播放文档自身的audio
        if (url && url !== this.audio.src) {
            this.audio.src = url;
        }
        this.playAudioActually();
    }

    /**
     *  创建对audio的reaction
     * @param isLoop 是否允许循环播放
     * @returns IReactionDisposer
     */
    private createAudioReaction(isLoop: boolean): IReactionDisposer {
        return reaction(
            /**
             * 直接监听 audioInstancesInPlayer 不行,因为在 set 或 delete 时 audioInstancesInPlayer 引用并不会变化
             * 所以 reaction 中的回调也不会触发
             */
            () => this.mediaManager.audioInstancesInPlayer.size,
            () => {
                if (this.isAlwaysPlayFirstAudio) return;
                this.mediaManager.audioInstancesInPlayer.forEach((audioObj: any, index) => {
                    if (!index) return;
                    this.audio = audioObj;
                });
                if (!this.audio) return;
                this.isAlwaysPlayFirstAudio = true;
                this.audio.loop = isLoop;
                if (this.audioUrlFromConfig) {
                    this.audio.src = this.audioUrlFromConfig;
                    // ios真机上不会触发音频load事件，需要手动load
                    this.audio.load();
                }
                // mediaManager中有值只表示audio元素已创建，但是未必已加载完成
                if (this.audio.readyState < AudioReadyState.HAVE_CURRENT_DATA) {
                    // 音频未加载完成则监听loaddata
                    this.audio.addEventListener(AUDIO_LOADED, this.onAudioLoaded);
                } else {
                    // 加载完成后，直接播放
                    this.playAudioActually();
                }
                this.audio.addEventListener(AUDIO_PAUSE_STATE, this.onAudioPause);
            },
        );
    }

    // 音频加载完成回调
    private onAudioLoaded = () => {
        this.playWhenSlideAudioCreated();
    };

    // 音频暂停回调
    private onAudioPause = () => {
        // 页面压入后台导致的音频暂停，不需要重新播放音频
        if (!this.isPageFocus) return;
        this.audio?.play()?.then(() => {
            window.log.info('play OK after pause');
        })
            ?.catch((err) => {
                window.log.info('play FAIL after pause', err);
            });
    };

    // 音频实际播放
    private playAudioActually() {
        // 如果音频已经播放了，audioHasPlayed为true, 无需重复播放
        if (!this.audio || this.audioHasPlayed) return;
        // 这个是临时代码，为了处理 dc 音频组件可能导致音频静音的问题，待 dc 修改之后这一行可以删除
        this.audio.muted = false;
        this.audio.play()?.then(() => {
            this.audioHasPlayed = true;
        })
            ?.catch((err) => {
                window.log.info('audio play fail', err);
            });
    }

    // 页面由压入后台到重新唤出，直接播放音频
    private handlePageFocus = () => {
        this.isPageFocus = true;
        this.playAudioActually();
    };

    // 页面压入后台，暂停音频
    private handlePageBlur = () => {
        // 失焦后需要audioHasPlayed置为false，这样页面重新唤出后才能够再次播放音频
        this.audioHasPlayed = false;
        this.isPageFocus = false;
        this.audio?.pause();
    };

    // 页面变化回调
    private onPageVisibleChange = () => {
        if (document.visibilityState === PAGE_VISIBLE) {
            this.handlePageFocus();
            return;
        }
        this.handlePageBlur();
    };

    // 添加页面监听
    private addPageChangeListener() {
        if (typeof document.hidden !== 'undefined') {
            // 浏览器支持 visibilitychange event
            document.addEventListener(PAGE_VISIBLE_CHANGE, this.onPageVisibleChange);
            return ;
        }
        // 浏览器不支持 visibilitychange event，则监听focus and blur events
        window.addEventListener(PAGE_FOCUS, this.handlePageFocus);
        window.addEventListener(PAGE_BLUR, this.handlePageBlur);
    }

    // 移除页面监听
    private removePageChangeListener() {
        if (typeof document.hidden !== 'undefined') {
            // 浏览器支持 visibilitychange event
            document.removeEventListener(PAGE_VISIBLE_CHANGE, this.onPageVisibleChange);
            return ;
        }
        // 浏览器不支持 visibilitychange event，则监听focus and blur events
        window.removeEventListener(PAGE_FOCUS, this.handlePageFocus);
        window.removeEventListener(PAGE_BLUR, this.handlePageBlur);
    }
}
